'use client';

/**
 * FloatingWrapper Component
 *
 * Wraps the Widget in a floating button + popup experience.
 * Used by <bolder-widget> web component for consistent rendering everywhere.
 *
 * Uses inline styles for Shadow DOM compatibility.
 *
 * Visual parity note: the outer chrome (FAB, greeting tooltip, unread badge,
 * panel border/radius/shadow, mobile sheet/fullscreen) is intentionally matched
 * to the in-app `FloatingAssistant` so an embedded widget looks identical to the
 * on-site assistant. The inner chat UI (header/messages/composer) is owned by
 * `Widget` (rendered into the Shadow DOM in production).
 *
 * Props:
 * - onlyPopup: When true, shows popup directly without floating button
 * - All other props are passed to Widget
 */

import { FC, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';

import { THEME_PRESETS } from '@/embed/hooks/useEmbedEditor';

import { ensureWidgetFontLoaded } from '../theme/fonts';
import { Widget, WidgetProps } from '../Widget';
import { widgetImageUrl } from '../widgetAttributes';

/**
 * How the widget sits on the host page:
 * - floating: launcher button + popup panel (default)
 * - inline: the chat fills the element it is placed in (a page section)
 * - fullpage: the chat fills the whole viewport, no outer chrome (hosted page / iframe)
 */
export type WidgetLayout = 'floating' | 'inline' | 'fullpage';

const PANEL_RADIUS: Record<string, string> = {
  none: '0px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  full: '24px',
};

export interface FloatingWrapperProps extends WidgetProps {
  /** Placement on the host page (see WidgetLayout) */
  layout?: WidgetLayout;
  /** Floating panel width in px (default 380) */
  panelWidth?: number;
  /** Floating panel height in px (default 600, capped to the viewport) */
  panelHeight?: number;
  /** Brand font id (theme/fonts.ts) — loaded on demand when it is a web font */
  fontId?: string;
  /** Preview only: render the mobile layout inside a contained (phone-sized) frame */
  previewDevice?: 'desktop' | 'mobile';
  /** Open the floating panel on mount (preview "open chat" state) */
  defaultOpen?: boolean;
  /** When true, shows popup directly without floating button */
  onlyPopup?: boolean;
  /** Position of the floating button */
  position?: 'bottom-left' | 'bottom-right';
  /** When true, renders Widget inside Shadow DOM for style isolation */
  useShadowDom?: boolean;
  /** When true, positions within parent container instead of viewport (for preview mode) */
  contained?: boolean;
  /** Edit controls to render attached to the widget (for editor mode) */
  editControls?: ReactNode;
  /** Optional text label rendered next to the floating button (turns the FAB into a pill) */
  floatingLabel?: string;
  /** Optional greeting tooltip shown ~2s after load while the panel is closed */
  greeting?: string;
  /** Mobile layout for the open panel: bottom sheet (default) or fullscreen */
  mobileMode?: 'sheet' | 'fullscreen';
  /** Number of unread assistant messages — renders a badge on the FAB when closed */
  unreadCount?: number;
  /** Chat button: the widget's picture (uploaded image, else the brain's) or the agent icon */
  buttonStyle?: 'picture' | 'icon';
}

// Styles for Shadow DOM widget container
const SHADOW_DOM_STYLES = `
  :host {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
  }
  .shadow-widget-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
`;

// Keyframes + responsive rules (injected into document head). The outer chrome
// renders in light DOM (portal container / contained parent), so a document-level
// stylesheet reaches it — the Shadow DOM only wraps the inner Widget.
const ANIMATION_STYLES_ID = 'bolder-floating-wrapper-styles';
const ANIMATION_STYLES = `
  @keyframes bolder-popup-enter {
    from { opacity: 0; transform: scale(0.95) translateY(20px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes bolder-button-enter {
    from { opacity: 0; transform: scale(0); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes bolder-greeting-enter {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes bolder-badge-pop {
    from { transform: scale(0); }
    to { transform: scale(1); }
  }
  .bolder-fw-fab { transition: transform 0.18s ease, box-shadow 0.2s ease; }
  .bolder-fw-fab:hover { transform: scale(1.05); box-shadow: 0 14px 40px -8px rgba(0,0,0,0.35); }
  .bolder-fw-fab:active { transform: scale(0.95); }
  @media (max-width: 640px) {
    .bolder-fw-fab[data-open="true"] { display: none !important; }
    .bolder-fw-panel[data-mobile="sheet"] {
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      height: 85dvh !important;
      max-height: 85dvh !important;
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }
    .bolder-fw-panel[data-mobile="fullscreen"] {
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      height: 100dvh !important;
      max-height: 100dvh !important;
      border-radius: 0 !important;
    }
  }
`;

function injectAnimationStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ANIMATION_STYLES_ID)) return;

  const style = document.createElement('style');
  style.id = ANIMATION_STYLES_ID;
  style.textContent = ANIMATION_STYLES;
  document.head.appendChild(style);
}

// Darken a hex color toward black by `amount` (0..1) — used to build the
// accent → primary gradient the in-app FAB uses, from the single embed accent.
function darkenHex(hex: string, amount: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const f = (h: string) => Math.max(0, Math.round(parseInt(h, 16) * (1 - amount)));
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(f(m[1]))}${toHex(f(m[2]))}${toHex(f(m[3]))}`;
}

// FAB glyphs (white line icons) — mirrors FloatingAssistant's flat-icon FAB.
function fabIcon(type: string) {
  const common = {
    width: 26,
    height: 26,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: '#ffffff',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (type) {
    case 'sparkle':
      return (
        <svg {...common}>
          <path d="M12 3l1.9 5.8L19.5 10l-5.6 1.2L12 17l-1.9-5.8L4.5 10l5.6-1.2L12 3z" />
        </svg>
      );
    case 'brain':
      return (
        <svg {...common}>
          <rect x="6" y="6" width="12" height="12" rx="3" />
          <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...common}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      );
    case 'message':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m3 7 9 6 9-6" />
        </svg>
      );
    case 'robot':
    default:
      return (
        <svg {...common}>
          <rect x="4" y="8" width="16" height="11" rx="3" />
          <circle cx="9" cy="13.5" r="1.3" fill="#ffffff" stroke="none" />
          <circle cx="15" cy="13.5" r="1.3" fill="#ffffff" stroke="none" />
          <path d="M12 4v4M9 4h6" />
        </svg>
      );
  }
}

export const FloatingWrapper: FC<FloatingWrapperProps> = memo(function FloatingWrapper({
  layout = 'floating',
  panelWidth,
  panelHeight,
  fontId,
  previewDevice = 'desktop',
  defaultOpen = false,
  onlyPopup: onlyPopupProp = false,
  position = 'bottom-right',
  useShadowDom = false,
  contained = false,
  editControls,
  floatingLabel,
  greeting,
  mobileMode = 'sheet',
  unreadCount = 0,
  theme,
  primaryColor,
  guestMode = false,
  agentIcon = 'robot',
  buttonStyle = 'picture',
  ...widgetProps
}) {
  // Inline and full-page layouts are always open and never show the launcher.
  const isEmbeddedLayout = layout === 'inline' || layout === 'fullpage';
  const onlyPopup = onlyPopupProp || isEmbeddedLayout;
  const mobilePreview = contained && previewDevice === 'mobile';

  // The chat button shows the widget's picture — the uploaded image, else the
  // brain's — and falls back to the icon when there is none or it fails to load.
  const pictureUrl =
    buttonStyle === 'picture'
      ? widgetImageUrl(
          widgetProps.displayImageOverride || widgetProps.selectedBrains?.[0]?.displayImage,
          widgetProps.mediaBaseUrl,
        )
      : '';
  const [failedPicture, setFailedPicture] = useState('');
  const launcherPicture = pictureUrl && pictureUrl !== failedPicture ? pictureUrl : '';

  // State
  const [isOpen, setIsOpen] = useState(onlyPopup || defaultOpen);
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const widgetKeyRef = useRef(0);
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const shadowReactRootRef = useRef<ReturnType<typeof import('react-dom/client').createRoot> | null>(null);

  // Greeting tooltip state — appears ~2s after load while the panel is closed.
  const [showGreeting, setShowGreeting] = useState(false);
  const greetingDismissedRef = useRef(false);

  // Close handler ref for Shadow DOM content
  const handleCloseRef = useRef(() => setIsOpen(false));
  handleCloseRef.current = () => setIsOpen(false);

  // Inject animation styles on mount
  useEffect(() => {
    injectAnimationStyles();
  }, []);

  useEffect(() => {
    ensureWidgetFontLoaded(fontId);
  }, [fontId]);

  // Initialize portal container (skip if contained mode)
  useEffect(() => {
    setMounted(true);

    // In contained mode (and in-place layouts), don't create portal - render directly
    if (contained || isEmbeddedLayout) {
      setPortalContainer(null);
      return () => setMounted(false);
    }

    let container = document.getElementById('bolder-floating-wrapper-portal') as HTMLElement | null;
    let createdContainer = false;

    if (!container) {
      container = document.createElement('div');
      container.id = 'bolder-floating-wrapper-portal';
      container.style.cssText = 'position: fixed; inset: 0; z-index: 9999; pointer-events: none;';
      document.body.appendChild(container);
      createdContainer = true;
    }

    setPortalContainer(container);

    return () => {
      setMounted(false);
      if (createdContainer && container?.parentElement) {
        container.parentElement.removeChild(container);
      }
    };
  }, [contained, isEmbeddedLayout]);

  // Compute theme values
  const { isDarkMode, themeColor } = useMemo(() => {
    if (typeof theme === 'object' && theme !== null) {
      const mode = theme.mode || 'light';
      const color = primaryColor || theme.primaryColor || '#3b82f6';
      return { isDarkMode: mode === 'dark', themeColor: color };
    }

    const themeStr = theme || 'default';
    const preset = THEME_PRESETS.find((p) => p.id === themeStr);

    if (preset) {
      return { isDarkMode: preset.mode === 'dark', themeColor: primaryColor || preset.color || '#3b82f6' };
    }

    return { isDarkMode: themeStr === 'dark', themeColor: primaryColor || '#3b82f6' };
  }, [theme, primaryColor]);

  // Theme-aware colors
  const popupBg = isDarkMode ? '#1f2937' : '#ffffff';
  const buttonBg = isDarkMode ? '#374151' : '#f3f4f6';
  const buttonHoverBg = isDarkMode ? '#4b5563' : '#e5e7eb';
  const buttonTextColor = isDarkMode ? '#9ca3af' : '#6b7280';
  const borderColor = isDarkMode ? '#374151' : '#e5e7eb';
  // Subtle panel border to match FloatingAssistant's `color-mix(... 10%)` outline.
  const panelBorderColor = isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
  const panelRadius =
    (typeof theme === 'object' && theme?.borderRadius && PANEL_RADIUS[theme.borderRadius]) || '16px';
  const fontFamily = typeof theme === 'object' && theme?.fontFamily ? theme.fontFamily : undefined;
  // accent → darker accent gradient, mirroring the in-app FAB's accent → primary.
  const fabGradient = `linear-gradient(135deg, ${themeColor}, ${darkenHex(themeColor, 0.55)})`;

  // Handle new conversation
  const handleNewConversation = useCallback(() => {
    widgetKeyRef.current += 1;
    setIsOpen(false);
    setTimeout(() => setIsOpen(true), 10);
  }, []);

  // Position styles - use larger offset in contained mode to position within preview bounds
  const offset = mobilePreview ? '16px' : contained ? '48px' : '24px';
  const positionStyle = position === 'bottom-left' ? { left: offset } : { right: offset };

  // If onlyPopup, always show open state
  useEffect(() => {
    if (onlyPopup) {
      setIsOpen(true);
    }
  }, [onlyPopup]);

  // Greeting tooltip: show after 2s if configured, not open, and not dismissed.
  useEffect(() => {
    if (!greeting || isOpen || onlyPopup || greetingDismissedRef.current) return;
    const timer = setTimeout(() => setShowGreeting(true), 2000);
    return () => clearTimeout(timer);
  }, [greeting, isOpen, onlyPopup]);

  const dismissGreeting = useCallback(() => {
    setShowGreeting(false);
    greetingDismissedRef.current = true;
  }, []);

  const openPanel = useCallback(() => {
    setIsOpen(true);
    setShowGreeting(false);
    greetingDismissedRef.current = true;
  }, []);

  // Render (or re-render) the Widget into the existing shadow React root.
  // Kept SEPARATE from root creation so prop changes only re-render — they must
  // never re-enter createRoot() on the same container (React throws "container
  // already passed to createRoot", which blanked the widget → the white preview
  // on every style change). Effect B below calls this when props change.
  const renderShadowWidget = useCallback(() => {
    const root = shadowReactRootRef.current;
    if (!root) return;
    // Drop onCollapse from widgetProps so it can't override our explicit one.
    const { onCollapse: _propsOnCollapse, ...restWidgetProps } = widgetProps as any;
    root.render(
      <Widget
        key={widgetKeyRef.current}
        {...restWidgetProps}
        theme={theme}
        primaryColor={primaryColor}
        guestMode={guestMode}
        agentIcon={agentIcon}
        showConfigureButton={false}
        // Pass onCollapse so the Widget's own header shows a collapse button.
        onCollapse={!onlyPopup ? () => handleCloseRef.current() : undefined}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          ...restWidgetProps.style,
        }}
      />,
    );
  }, [widgetProps, theme, primaryColor, guestMode, agentIcon, onlyPopup]);

  // Effect A — create the shadow root + React root, and tear them down on
  // close/unmount. Depends ONLY on [useShadowDom, isOpen] so it never re-runs
  // (and never re-roots) when props change.
  useEffect(() => {
    if (!useShadowDom || !shadowHostRef.current || !isOpen) return;

    // attachShadow can only be called once per element — reuse if present
    // (handles React Strict Mode's double-invoke).
    let shadowRoot = shadowHostRef.current.shadowRoot;
    if (!shadowRoot) {
      shadowRoot = shadowHostRef.current.attachShadow({ mode: 'open' });
      const styleSheet = document.createElement('style');
      styleSheet.textContent = SHADOW_DOM_STYLES;
      shadowRoot.appendChild(styleSheet);
    }
    shadowRootRef.current = shadowRoot;

    let container = shadowRoot.querySelector('.shadow-widget-container') as HTMLElement | null;
    if (!container) {
      container = document.createElement('div');
      container.className = 'shadow-widget-container';
      shadowRoot.appendChild(container);
    }
    if (!shadowReactRootRef.current) {
      shadowReactRootRef.current = createRoot(container);
    }

    return () => {
      // Unmount asynchronously so we don't unmount mid-commit. We deliberately
      // leave the container/shadow root attached — the next open reuses them.
      const rootToUnmount = shadowReactRootRef.current;
      shadowReactRootRef.current = null;
      if (rootToUnmount) {
        setTimeout(() => {
          try {
            rootToUnmount.unmount();
          } catch {
            // already unmounted
          }
        }, 0);
      }
    };
    // `mounted` is a dependency because the host only exists once the panel has
    // rendered: a panel that starts open (inline / full page / defaultOpen) mounts
    // its host one render after this effect first runs.
  }, [useShadowDom, isOpen, mounted]);

  // Effect B — paint the Widget into the root, and re-paint when props change.
  // Runs after Effect A (declared later), so the root already exists.
  useEffect(() => {
    if (!useShadowDom || !isOpen || !mounted) return;
    renderShadowWidget();
  }, [useShadowDom, isOpen, mounted, renderShadowWidget]);

  // Button hover state (non-shadow control row)
  const [newBtnHover, setNewBtnHover] = useState(false);
  const [userBtnHover, setUserBtnHover] = useState(false);
  const [closeBtnHover, setCloseBtnHover] = useState(false);

  // SVG icons for the non-shadow control row
  const PenIcon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={buttonTextColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );

  const UserIcon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={buttonTextColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  const CloseIcon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={buttonTextColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  // Common position type for both modes
  const positionType = contained ? 'absolute' : 'fixed';
  const popupWidth = `${panelWidth ?? 380}px`;
  const popupHeight = contained
    ? `min(${panelHeight ?? 600}px, calc(100% - 140px))`
    : `min(${panelHeight ?? 600}px, calc(100vh - 120px))`;

  // A phone-sized preview frame can't trigger the @media rules, so the mobile
  // sheet / fullscreen layout is applied inline there.
  const mobilePanelStyle: React.CSSProperties | null = mobilePreview
    ? mobileMode === 'fullscreen'
      ? { left: 0, right: 0, bottom: 0, width: '100%', maxWidth: '100%', height: '100%' }
      : { left: 0, right: 0, bottom: 0, width: '100%', maxWidth: '100%', height: '85%' }
    : null;
  const mobileInnerStyle: React.CSSProperties | null = mobilePreview
    ? mobileMode === 'fullscreen'
      ? { borderRadius: 0 }
      : { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
    : null;

  // Greeting tooltip — appears above the FAB while closed.
  const greetingTooltip = greeting && showGreeting && !isOpen && mounted && (
    <div
      style={{
        position: positionType,
        bottom: contained ? '110px' : '96px',
        ...positionStyle,
        zIndex: 9997,
        maxWidth: '240px',
        pointerEvents: 'auto',
        animation: 'bolder-greeting-enter 0.2s ease-out',
      }}
    >
      <div
        style={{
          position: 'relative',
          background: popupBg,
          color: isDarkMode ? '#e5e7eb' : '#333333',
          border: `1px solid ${panelBorderColor}`,
          borderRadius: '12px',
          padding: '10px 14px',
          fontSize: '14px',
          lineHeight: 1.4,
          fontFamily,
          boxShadow: '0 6px 24px -8px rgba(0,0,0,0.25)',
        }}
      >
        {greeting}
        <button
          onClick={dismissGreeting}
          aria-label="Dismiss"
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-6px',
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            background: popupBg,
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke={buttonTextColor}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );

  // Floating button — persistent gradient FAB. While the panel is open it
  // toggles to an X so there's always a close affordance at the bottom (matching
  // the in-app FloatingAssistant); on mobile it's hidden while open (CSS rule)
  // so it doesn't overlap the composer.
  const collapsedButton = !onlyPopup && mounted && !(mobilePreview && isOpen) && (
    <div
      style={{
        position: positionType,
        bottom: offset,
        ...positionStyle,
        zIndex: 9998,
        pointerEvents: 'auto',
        animation: 'bolder-button-enter 0.2s ease-out',
      }}
    >
      {/* Edit controls - pinned to top-left of the FAB (only while collapsed) */}
      {editControls && !isOpen && (
        <div style={{ position: 'absolute', top: '-8px', left: '-8px', zIndex: 10 }}>{editControls}</div>
      )}
      <button
        className="bolder-fw-fab"
        data-open={isOpen ? 'true' : 'false'}
        onClick={() => (isOpen ? setIsOpen(false) : openPanel())}
        aria-label={isOpen ? 'Close AI assistant' : 'Open AI assistant'}
        style={{
          position: 'relative',
          cursor: 'pointer',
          border: 'none',
          padding: floatingLabel && !isOpen ? '0 18px' : 0,
          height: '56px',
          width: floatingLabel && !isOpen ? 'auto' : '56px',
          minWidth: '56px',
          borderRadius: '9999px',
          background: fabGradient,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: floatingLabel && !isOpen ? '8px' : 0,
          boxShadow: '0 10px 30px -8px rgba(0,0,0,0.30)',
          fontFamily,
        }}
      >
        {isOpen ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : launcherPicture ? (
          // eslint-disable-next-line @next/next/no-img-element -- runs on customer sites, outside Next
          <img
            src={launcherPicture}
            alt=""
            onError={() => setFailedPicture(launcherPicture)}
            style={{
              width: floatingLabel ? '36px' : '56px',
              height: floatingLabel ? '36px' : '56px',
              marginLeft: floatingLabel ? '-8px' : 0,
              borderRadius: '9999px',
              objectFit: 'cover',
              background: '#ffffff',
              boxShadow: floatingLabel ? '0 0 0 2px rgba(255,255,255,0.9)' : 'none',
              display: 'block',
              flexShrink: 0,
            }}
          />
        ) : (
          fabIcon(agentIcon)
        )}
        {floatingLabel && !isOpen && (
          <span style={{ fontSize: '14px', fontWeight: 500 }}>{floatingLabel}</span>
        )}

        {/* Unread badge (collapsed only) */}
        {unreadCount > 0 && !isOpen && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '20px',
              height: '20px',
              padding: '0 5px',
              borderRadius: '9999px',
              background: '#ef4444',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              animation: 'bolder-badge-pop 0.2s ease-out',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );

  // The chat itself: a Shadow DOM host (Widget is painted into it by the effects
  // above) or the Widget directly.
  const widgetBody =
    isOpen && mounted ? (
      useShadowDom ? (
        /* Shadow DOM host - Widget is rendered inside via useEffect */
        <div
          ref={shadowHostRef}
          style={{
            width: '100%',
            height: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        />
      ) : (
        /* Direct Widget rendering */
        <Widget
          key={widgetKeyRef.current}
          theme={theme}
          primaryColor={primaryColor}
          guestMode={guestMode}
          agentIcon={agentIcon}
          showConfigureButton={false}
          {...widgetProps}
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            ...widgetProps.style,
          }}
        />
      )
    ) : null;

  // Expanded state - Widget Popup content
  const expandedPopup = isOpen && mounted && (
    <div style={{ pointerEvents: 'auto' }}>
      <div
        className="bolder-fw-panel"
        data-mobile={mobileMode}
        style={{
          position: positionType,
          // Sit above the persistent FAB (56px + gap) so they don't overlap.
          bottom: contained ? '116px' : '88px',
          ...positionStyle,
          zIndex: 9999,
          width: popupWidth,
          maxWidth: contained ? 'calc(100% - 32px)' : 'calc(100vw - 32px)',
          // The height lives on this positioned box: a percentage here resolves against
          // the preview frame (contained) and vh against the viewport; the inner box fills it.
          height: popupHeight,
          animation: onlyPopup ? 'none' : 'bolder-popup-enter 0.25s ease-out',
          ...(mobilePanelStyle || {}),
        }}
      >
        {/* Edit controls - pinned to top-left of expanded popup */}
        {editControls && (
          <div style={{ position: 'absolute', top: '0px', left: '-8px', zIndex: 10 }}>{editControls}</div>
        )}

        {/* Main popup container */}
        <div
          style={{
            position: 'relative',
            background: popupBg,
            borderRadius: panelRadius,
            border: `1px solid ${panelBorderColor}`,
            overflow: 'hidden',
            boxShadow: '0 24px 60px -15px rgba(0, 0, 0, 0.35)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            ...(mobileInnerStyle || {}),
          }}
        >
          {/* Control buttons row - hidden when using Shadow DOM (Widget has its own header) */}
          {!useShadowDom && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                borderBottom: `1px solid ${borderColor}`,
              }}
            >
              {/* New conversation button */}
              <button
                onClick={handleNewConversation}
                title="New conversation"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: newBtnHover ? buttonHoverBg : buttonBg,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={() => setNewBtnHover(true)}
                onMouseLeave={() => setNewBtnHover(false)}
              >
                {PenIcon}
              </button>

              {/* Sign in button (hidden in guest mode) */}
              {!guestMode && (
                <button
                  title="Sign in"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: userBtnHover ? buttonHoverBg : buttonBg,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={() => setUserBtnHover(true)}
                  onMouseLeave={() => setUserBtnHover(false)}
                >
                  {UserIcon}
                </button>
              )}

              {/* Close button (hidden if onlyPopup) */}
              {!onlyPopup && (
                <button
                  onClick={() => setIsOpen(false)}
                  title="Close"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: closeBtnHover ? buttonHoverBg : buttonBg,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={() => setCloseBtnHover(true)}
                  onMouseLeave={() => setCloseBtnHover(false)}
                >
                  {CloseIcon}
                </button>
              )}
            </div>
          )}

          {/* Widget content */}
          <div
            style={{
              minHeight: contained ? '240px' : '320px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {widgetBody}
          </div>
        </div>
      </div>
    </div>
  );

  // Inline / full page: the chat fills the element it was placed in.
  if (isEmbeddedLayout) {
    const isFullPage = layout === 'fullpage';
    return (
      <div
        className="bolder-fw-embedded"
        data-layout={layout}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          minHeight: isFullPage ? 0 : '420px',
          background: popupBg,
          borderRadius: isFullPage ? 0 : panelRadius,
          border: isFullPage ? 'none' : `1px solid ${panelBorderColor}`,
          overflow: 'hidden',
          fontFamily,
        }}
      >
        {editControls && (
          <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10 }}>{editControls}</div>
        )}
        {widgetBody}
      </div>
    );
  }

  // In contained mode, render directly without portals
  if (contained) {
    return (
      <>
        {greetingTooltip}
        {collapsedButton}
        {expandedPopup}
      </>
    );
  }

  // In normal mode, use portals for fixed positioning
  return (
    <>
      {greetingTooltip && portalContainer && createPortal(greetingTooltip, portalContainer)}
      {collapsedButton && portalContainer && createPortal(collapsedButton, portalContainer)}
      {expandedPopup && portalContainer && createPortal(expandedPopup, portalContainer)}
    </>
  );
});

export default FloatingWrapper;
